import {
  FetchError,
  AuthenticationError,
  AuthorizationError,
  HttpError,
  NotFoundError,
  RateLimitError,
  ValidationError,
} from "./errors";

export interface ExtendedResponse<X> extends Response {
  data: X;
}

export interface ExtendedRequestInit<X> extends RequestInit {
  expectedStatuses?: number[];
  parser?: (response: Response) => X;
  suppressBackendErrorLog?: boolean;
}

type BackendErrorLogPayload = {
  id: string;
  time: string;
  service: string;
  method: string;
  url: string;
  status: number;
  statusText: string;
  durationMs: number;
  message: string;
  request?: {
    headers?: unknown;
    body?: unknown;
  };
  response?: unknown;
  error?: {
    name?: string;
    message?: string;
    stack?: string;
    cause?: unknown;
  };
};

export default async function fetcher<X>(
  input: RequestInfo | URL,
  init?: ExtendedRequestInit<X>,
): Promise<ExtendedResponse<X>> {
  const startedAt = Date.now();
  const requestUrl = getRequestUrl(input);
  const requestMethod = init?.method ?? "GET";

  try {
    const response = await fetch(input, {
      ...createDefaultInit(),
      ...init,
      headers: {
        ...createDefaultHeaders(init?.headers),
      },
    });

    const parser = init?.parser;
    const data: X = parser ? parser(response) : await response.json();

    if (!response.ok || data === null) {
      reportBackendError({
        input,
        init,
        requestUrl,
        requestMethod,
        startedAt,
        response,
        data,
        message: !response.ok
          ? `${response.status} ${response.statusText}`
          : "Backend response data is null",
      });

      if (!response.ok) createFetchError("", response, data);
    }

    const extendedResponse = createResponse(response, data);

    return extendedResponse;
  } catch (err) {
    if (err instanceof FetchError)
      return createResponse(err.response, err.data);

    reportBackendError({
      input,
      init,
      requestUrl,
      requestMethod,
      startedAt,
      error: err,
      message: err instanceof Error ? err.message : "Fetch failed",
    });
  }

  const fallbackResponse = new Response(null, {
    status: 500,
    statusText: "Internal Server Error",
  });
  return createResponse(fallbackResponse, null as X);
}
export function createFetchError<X>(
  message: string,
  response: Response,
  data: X,
): FetchError<X> {
  switch (response.status) {
    case 401:
      return new AuthenticationError(message, response, data);
    case 403:
      return new AuthorizationError(message, response, data);
    case 404:
      return new NotFoundError(message, response, data);
    case 429:
      return new RateLimitError(message, response, data);
    case 422:
      return new ValidationError(message, response, data);
    default:
      return new HttpError(message, response, data);
  }
}

function createDefaultHeaders(headers?: HeadersInit): HeadersInit {
  return {
    "X-Server-Auth": process.env.X_SERVER_AUTH ?? "",
    ...headers,
  };
}

function createDefaultInit(): RequestInit {
  return {
    cache: "no-cache",
  };
}

function createResponse<X>(response: Response, data: X) {
  return Object.assign(response, { data });
}

function reportBackendError<X>({
  input,
  init,
  requestUrl,
  requestMethod,
  startedAt,
  response,
  data,
  error,
  message,
}: {
  input: RequestInfo | URL;
  init?: ExtendedRequestInit<X>;
  requestUrl: string;
  requestMethod: string;
  startedAt: number;
  response?: Response;
  data?: X;
  error?: unknown;
  message: string;
}) {
  if (!isBackendErrorLoggingEnabled()) return;
  if (init?.suppressBackendErrorLog) return;
  if (requestUrl.includes("/api/log/backend-error")) return;
  if (isExpectedStatus(response, init?.expectedStatuses)) return;
  if (isExpectedProfileAuthMiss(requestUrl, response)) return;

  const payload: BackendErrorLogPayload = {
    id: createBackendErrorId(),
    time: new Date().toISOString(),
    service: inferServiceFromUrl(requestUrl),
    method: requestMethod,
    url: requestUrl,
    status: response?.status ?? 0,
    statusText: response?.statusText ?? "",
    durationMs: Date.now() - startedAt,
    message,
    request: {
      headers: maskSensitiveValue(createPlainHeaders(init?.headers)),
      body: serializeBody(init?.body),
    },
    response: maskSensitiveValue(data),
    error: serializeError(error),
  };

  void fetch(getBackendErrorLogEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
}

function getRequestUrl(input: RequestInfo | URL) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

const logEndpoint = "/api/log/backend-error/";

function getBackendErrorLogEndpoint() {
  if (typeof window !== "undefined") return logEndpoint;

  return (
    process.env.BACKEND_ERROR_LOG_ENDPOINT ??
    `http://localhost:${process.env.PORT ?? "3000"}${logEndpoint}`
  );
}

function isBackendErrorLoggingEnabled() {
  return (
    process.env.ENABLE_BACKEND_ERROR_LOGS === "true" ||
    process.env.NODE_ENV === "development"
  );
}

function inferServiceFromUrl(url: string) {
  if (url.includes("/hotel/")) return "hotel";
  if (url.includes("/flight/")) return "flight";
  if (url.includes("/tour/")) return "tour";
  if (url.includes("/profile/") || url.includes("/user/profile")) {
    return "profile";
  }
  if (url.includes("/settings")) return "settings";
  if (url.includes("/content/")) return "content";

  return "public";
}

function isExpectedStatus(response?: Response, expectedStatuses?: number[]) {
  return Boolean(
    response &&
      expectedStatuses?.some(
        (expectedStatus) => expectedStatus === response.status,
      ),
  );
}

function isExpectedProfileAuthMiss(url: string, response?: Response) {
  return response?.status === 401 && url.includes("/v1/user/profile");
}

function createBackendErrorId() {
  const stamp = new Date()
    .toISOString()
    .replace(/[-:.TZ]/g, "")
    .slice(0, 14);
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 6)
      : Math.random().toString(36).slice(2, 8);

  return `BE-${stamp}-${random.toUpperCase()}`;
}

function createPlainHeaders(headers?: HeadersInit) {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);

  return headers;
}

function serializeBody(body?: BodyInit | null) {
  if (!body) return undefined;
  if (typeof body === "string") return tryParseJson(body);
  if (body instanceof URLSearchParams) return Object.fromEntries(body.entries());
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return Object.fromEntries(body.entries());
  }

  return "[unserializable body]";
}

function tryParseJson(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function serializeError(error: unknown) {
  if (!(error instanceof Error)) return undefined;

  return {
    name: error.name,
    message: error.message,
    stack: error.stack,
    cause: "cause" in error ? error.cause : undefined,
  };
}

function maskSensitiveValue<T>(value: T): T {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(maskSensitiveValue) as T;

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => {
      if (isSensitiveKey(key)) return [key, "***masked***"];

      return [key, maskSensitiveValue(nestedValue)];
    }),
  ) as T;
}

function isSensitiveKey(key: string) {
  return /authorization|token|password|x-server-auth|cookie|national|passport|identifier/i.test(
    key,
  );
}
